import { useState, useEffect, useCallback } from 'react';
import type {
  ComponentContentSchema,
  ComponentDesignSchema,
  SlotGroupDefinition,
} from '@vibesites/runtime';
import {
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
  useContentToken,
  useSlotGroup,
  usePropertyAddressUrl,
  useEditMode,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// sessionStorage flag so the popup shows only once per live visit (cleared when
// the browser tab/session ends). In the editor we skip this so the popup stays
// available on every refresh.
const SEEN_KEY = 'las-palmas-promo-popup-seen';

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
    },
    url: {
      label: 'Link URL',
      type: 'url',
      defaultValue: '',
      description: 'Optional. Defaults to the property map / directions link.',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

/**
 * Split at the first comma so street stays on line 1 and city/state/zip stay
 * together on line 2 when the full address won't fit on one line.
 */
function splitAddress(fullAddress: string): { street: string; locality: string } {
  const trimmed = fullAddress.trim();
  const idx = trimmed.indexOf(',');
  if (idx === -1) return { street: trimmed, locality: '' };
  return {
    street: trimmed.slice(0, idx).trim(),
    locality: trimmed.slice(idx + 1).trim(),
  };
}

function AddressLines({ address }: { address: string }) {
  const { street, locality } = splitAddress(address);
  if (!street) return null;
  return (
    <span className="lppp-address">
      <span className="lppp-address-street">
        {street}
        {locality ? ',' : ''}
      </span>
      {locality ? (
        <>
          {' '}
          <span className="lppp-address-locality">{locality}</span>
        </>
      ) : null}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PromoPopup() {
  const image = useContentToken('promopopup.image');
  const addressLink = useSlotGroup('promopopup.address', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  // Closed during SSR / first paint, then opened on mount so it shows without
  // flashing in the static HTML. Live visitors: once per session. Editor: every
  // refresh (and dismissals are not remembered).
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      const raf = requestAnimationFrame(() => setOpen(true));
      return () => cancelAnimationFrame(raf);
    }
    let seen = false;
    try {
      seen = window.sessionStorage.getItem(SEEN_KEY) === '1';
    } catch {
      // sessionStorage unavailable — show rather than suppress.
    }
    if (seen) return;
    const raf = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(raf);
  }, [isEditMode]);

  const close = useCallback(() => {
    setOpen(false);
    // Don't persist dismissals while editing — refresh should bring it back.
    if (isEditMode) return;
    try {
      window.sessionStorage.setItem(SEEN_KEY, '1');
    } catch {
      // Ignore storage failures — the popup simply won't be remembered.
    }
  }, [isEditMode]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open) return null;

  const addressText = (addressLink.address || '').trim();
  const addressHref =
    (addressLink.url || '').trim() ||
    propertyAddressUrl ||
    (addressText
      ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressText)}`
      : '');

  return (
    <div
      className="lppp-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Open House"
      onClick={close}
    >
      <div className="lppp-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="lppp-close" onClick={close} aria-label="Close">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="lppp-card">
          {/* Background photo — position lives on the parent, not EditableToken */}
          <div className="lppp-bg">
            <EditableToken
              path="promopopup.image"
              type="image"
              as="div"
              label="Background Image"
              className="lppp-bg-token"
            >
              <VibesitesImage
                url={image || ''}
                alt=""
                priority
                sizes="(max-width: 640px) 100vw, 560px"
                className="lppp-bg-img"
              />
            </EditableToken>
          </div>

          <div className="lppp-panel">
            <div className="lppp-panel-fill" aria-hidden="true" />
            <div className="lppp-frame" aria-hidden="true" />

            <div className="lppp-copy">
              <RichTextContent
                path="promopopup.headline"
                label="Headline"
                as="h2"
                className="lppp-headline font-heading"
                defaultValue="<p>Las Palmas</p>"
              />

              <RichTextContent
                path="promopopup.body"
                label="Offer Text"
                as="div"
                className="lppp-body font-body"
                defaultValue="<p>Open House</p><p>Saturday 8/15</p><p>10AM - 3PM</p>"
              />

              {addressText ? (
                <EditableSlotGroup groupPath="promopopup.address" definition={addressLinkDef}>
                  {addressHref ? (
                    <a
                      href={addressHref}
                      target={addressLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="lppp-address-link"
                    >
                      <AddressLines address={addressText} />
                    </a>
                  ) : (
                    <span className="lppp-address-link">
                      <AddressLines address={addressText} />
                    </span>
                  )}
                </EditableSlotGroup>
              ) : (
                <EditableSlotGroup
                  groupPath="promopopup.address"
                  definition={addressLinkDef}
                  className="lppp-address-empty"
                >
                  <span className="lppp-address-placeholder">Add address</span>
                </EditableSlotGroup>
              )}

              <RichTextContent
                path="promopopup.footer"
                label="Footer"
                as="div"
                className="lppp-footer font-body"
                defaultValue="<p>We look forward to seeing you there!</p>"
              />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .lppp-overlay {
          position: fixed;
          inset: 0;
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.25rem;
          background: rgba(0, 0, 0, 0);
          animation: lppp-overlay-in 0.3s ease forwards;
        }
        @keyframes lppp-overlay-in {
          to { background: rgba(0, 0, 0, 0.62); }
        }

        .lppp-modal {
          position: relative;
          width: min(100%, 560px);
          opacity: 0;
          transform: translateY(16px) scale(0.97);
          animation: lppp-modal-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) 0.05s forwards;
        }
        @keyframes lppp-modal-in {
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .lppp-card {
          position: relative;
          aspect-ratio: 1 / 1;
          width: 100%;
          overflow: hidden;
          box-shadow: 0 24px 64px rgba(0, 0, 0, 0.4);
        }

        .lppp-bg {
          position: absolute;
          inset: 0;
        }
        .lppp-bg-token {
          display: block;
          width: 100%;
          height: 100%;
        }
        .lppp-bg-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .lppp-panel {
          position: absolute;
          inset: 8%;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: clamp(1rem, 3.5vw, 1.75rem);
          text-align: center;
        }
        /* Defaults to site primary; override via design tokens (Panel Color / Opacity). */
        .lppp-panel-fill {
          position: absolute;
          inset: 0;
          background: var(--lppp-panel-color, var(--vibe-color-primary));
          opacity: var(--lppp-panel-opacity, 0.78);
          pointer-events: none;
        }

        /* Crosshair / technical-drawing frame — matches text-on-primary for contrast */
        .lppp-frame {
          position: absolute;
          inset: 10px;
          pointer-events: none;
          --lppp-frame: var(--vibe-color-text-on-primary, #fff);
          background:
            linear-gradient(var(--lppp-frame), var(--lppp-frame)) top left / 22px 1.5px no-repeat,
            linear-gradient(var(--lppp-frame), var(--lppp-frame)) top left / 1.5px 22px no-repeat,
            linear-gradient(var(--lppp-frame), var(--lppp-frame)) top right / 22px 1.5px no-repeat,
            linear-gradient(var(--lppp-frame), var(--lppp-frame)) top right / 1.5px 22px no-repeat,
            linear-gradient(var(--lppp-frame), var(--lppp-frame)) bottom left / 22px 1.5px no-repeat,
            linear-gradient(var(--lppp-frame), var(--lppp-frame)) bottom left / 1.5px 22px no-repeat,
            linear-gradient(var(--lppp-frame), var(--lppp-frame)) bottom right / 22px 1.5px no-repeat,
            linear-gradient(var(--lppp-frame), var(--lppp-frame)) bottom right / 1.5px 22px no-repeat;
          box-shadow: inset 0 0 0 1.5px var(--lppp-frame);
        }

        .lppp-copy {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          width: 100%;
          max-width: 100%;
        }

        .lppp-headline {
          margin: 0;
          color: var(--vibe-color-text-on-primary, #fff);
          font-size: clamp(1.85rem, 5.5vw, 2.65rem);
          font-weight: 700;
          line-height: 1.05;
          letter-spacing: -0.02em;
        }
        .lppp-headline p {
          margin: 0;
        }

        .lppp-body {
          margin: 0;
          color: var(--vibe-color-text-on-primary, #fff);
          font-size: clamp(0.95rem, 2.6vw, 1.15rem);
          font-weight: 700;
          line-height: 1.35;
        }
        .lppp-body p {
          margin: 0;
        }

        .lppp-address-link {
          display: block;
          width: 100%;
          max-width: 100%;
          color: var(--vibe-color-text-on-primary, #fff);
          font-family: var(--vibe-font-body, inherit);
          font-size: clamp(0.85rem, 2.2vw, 1rem);
          font-weight: 700;
          line-height: 1.35;
          text-decoration: underline;
          text-underline-offset: 0.18em;
          text-decoration-thickness: 1.5px;
          transition: opacity 0.15s;
        }
        a.lppp-address-link:hover {
          opacity: 0.7;
        }

        /* Prefer one line; if it wraps, break before the city — never orphan the zip. */
        .lppp-address {
          display: block;
        }
        .lppp-address-street,
        .lppp-address-locality {
          white-space: nowrap;
        }

        .lppp-address-empty {
          display: block;
        }
        .lppp-address-placeholder {
          display: inline-block;
          padding: 0.35rem 0.6rem;
          border: 1px dashed color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 35%, transparent);
          color: color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 55%, transparent);
          font-size: 0.75rem;
          font-weight: 600;
        }

        .lppp-footer {
          margin: 0.15rem 0 0;
          color: var(--vibe-color-text-on-primary, #fff);
          font-size: clamp(0.7rem, 1.8vw, 0.82rem);
          font-weight: 400;
          line-height: 1.35;
          opacity: 0.9;
        }
        .lppp-footer p {
          margin: 0;
        }

        /* Inline links from richtext (headline / body / footer) */
        .lppp-headline a,
        .lppp-body a,
        .lppp-footer a {
          color: inherit;
          font-weight: 700;
          text-decoration: underline;
          text-underline-offset: 0.15em;
          text-decoration-thickness: 1.5px;
          transition: opacity 0.15s;
        }
        .lppp-headline a:hover,
        .lppp-body a:hover,
        .lppp-footer a:hover {
          opacity: 0.7;
        }

        .lppp-close {
          position: absolute;
          top: -14px;
          right: -14px;
          z-index: 3;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: none;
          background: #fff;
          color: #1a1a1a;
          cursor: pointer;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
          transition: transform 0.2s, background 0.2s, color 0.2s;
        }
        .lppp-close:hover {
          transform: scale(1.08);
          background: var(--vibe-color-primary, #1a1a1a);
          color: var(--vibe-color-text-on-primary, #fff);
        }

        @media (max-width: 640px) {
          .lppp-overlay { padding: 0.85rem; }
          .lppp-modal { width: min(100%, 420px); }
          .lppp-panel { inset: 6%; padding: 0.9rem; }
          .lppp-frame { inset: 8px; }
          .lppp-copy { gap: 0.6rem; }
          .lppp-close {
            top: 8px;
            right: 8px;
            width: 34px;
            height: 34px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .lppp-overlay { animation: none; background: rgba(0, 0, 0, 0.62); }
          .lppp-modal { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PromoPopup',
  displayName: 'Promo Popup',
  tokens: {
    'promopopup.image': {
      label: 'Background Image',
      type: 'image',
      defaultField: 'property_featured_image',
      defaultValue: '',
    },
    'promopopup.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue: '<p>Las Palmas</p>',
    },
    'promopopup.body': {
      label: 'Offer Text',
      type: 'richtext',
      defaultValue: '<p>Open House</p><p>Saturday 8/15</p><p>10AM - 3PM</p>',
    },
    'promopopup.footer': {
      label: 'Footer',
      type: 'richtext',
      defaultValue: '<p>We look forward to seeing you there!</p>',
    },
  },
  slotGroups: {
    'promopopup.address': addressLinkDef,
  },
};

(PromoPopup as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

// ─── Design Schema (panel tint) ───────────────────────────────────────────────

export const designSchema: ComponentDesignSchema = {
  slot: 'PromoPopup',
  displayName: 'Promo Popup',
  tokens: {
    'color.panel': {
      label: 'Panel Color',
      type: 'color',
      cssVariable: '--lppp-panel-color',
      description: 'Tint for the centered panel. Defaults to the site primary color when unset.',
    },
    'opacity.panel': {
      label: 'Panel Opacity',
      type: 'opacity',
      cssVariable: '--lppp-panel-opacity',
      defaultValue: '0.78',
      min: 0.2,
      max: 1,
      step: 0.02,
      description: 'How transparent the panel tint appears over the photo.',
    },
  },
};

(PromoPopup as unknown as { designSchema: ComponentDesignSchema }).designSchema =
  designSchema;

export default PromoPopup;
